import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateRangeType } from "../../ui/DateRangeSelector";
import { dispatchStatusFilter, orderStatusFilter } from "./util";

// ---------------------------------------------------------------------------
// useOrderFilters
//
// Single source of truth for the Order Management filter state.
// Owns:
//   - Typed-vs-committed state for the search input (explicit-commit on
//     Enter / filter button — preserves the existing operator UX).
//   - Categorical (status, dispatch status) + date range filter state.
//   - Pagination (page / limit), 0-based for the UI.
//   - URL <-> state sync (so filters survive refresh + nav-away/back +
//     deep links like /order-management/all-orders?status=COMPLETED).
//   - "Reset key" for force-remounting controls that seed from props on
//     mount only (FilterByButton, DateRangeSelector).
//
// All filter setters auto-reset `page` to 0 — changing a filter should
// always return the user to the first page of results.
//
// Search is intentionally NOT debounced. The order table is a wide ops view
// where admins type once and commit explicitly (Enter / filter button) —
// matching pre-refactor behavior.
// ---------------------------------------------------------------------------

/**
 * URL parameter keys this hook owns. The sync effect deletes these from the
 * existing URL before re-setting active ones so it never clobbers params
 * managed by the parent page (e.g. a future tab indicator).
 */
const MANAGED_URL_KEYS = [
  "search",
  "status",
  "dispatchStatus",
  "startDate",
  "endDate",
  "page",
  "limit",
] as const;

const isValidOrderStatus = (val: string): boolean =>
  orderStatusFilter.some((opt) => opt.value === val);

const isValidDispatchStatus = (val: string): boolean =>
  dispatchStatusFilter.some((opt) => opt.value === val);

const parseUrlDate = (val: string | null): Date | null => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseFilters = (sp: URLSearchParams) => {
  const status = sp.get("status");
  const dispatchStatus = sp.get("dispatchStatus");
  const page = Number(sp.get("page"));
  const limit = Number(sp.get("limit"));

  return {
    search: sp.get("search") ?? "",
    status: status && isValidOrderStatus(status) ? status : "",
    dispatchStatus:
      dispatchStatus && isValidDispatchStatus(dispatchStatus)
        ? dispatchStatus
        : "",
    dateRange: {
      startDate: parseUrlDate(sp.get("startDate")),
      endDate: parseUrlDate(sp.get("endDate")),
    } as DateRangeType,
    page: Number.isFinite(page) && page > 0 ? page : 0,
    limit: Number.isFinite(limit) && limit > 0 ? limit : 10,
  };
};

export interface UseOrderFiltersReturn {
  // Typed input state (controlled in the search bar)
  inputValue: string;
  setInputValue: (v: string) => void;

  // Applied filter state
  search: string;
  /** Push the current `inputValue` into `search` (writes to URL, refetches). */
  commitSearch: () => void;
  status: string;
  setStatus: (v: string) => void;
  dispatchStatus: string;
  setDispatchStatus: (v: string) => void;
  dateRange: DateRangeType;
  setDateRange: (r: DateRangeType) => void;

  // Pagination (0-based)
  page: number;
  setPage: (v: number) => void;
  limit: number;
  setLimit: (v: number) => void;

  // Bar chrome helpers
  filterResetKey: number;
  bumpFilterResetKey: () => void;
  handleClearFilters: () => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
}

export const useOrderFilters = (): UseOrderFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params ONCE at mount. Subsequent URL changes are driven by the
  // sync effect below — we never re-hydrate from URL after mount.
  const initial = useMemo(
    () => parseFilters(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [inputValue, setInputValue] = useState(initial.search);
  const [search, setSearchState] = useState(initial.search);
  const [status, setStatusState] = useState(initial.status);
  const [dispatchStatus, setDispatchStatusState] = useState(
    initial.dispatchStatus
  );
  const [dateRange, setDateRangeState] = useState<DateRangeType>(
    initial.dateRange
  );
  const [page, setPage] = useState(initial.page);
  const [limit, setLimitState] = useState(initial.limit);
  // Bumping this key force-remounts FilterByButton / DateRangeSelector,
  // which both seed internal state from props on mount only.
  const [filterResetKey, setFilterResetKey] = useState(0);

  const commitSearch = useCallback(() => {
    setSearchState(inputValue);
    setPage(0);
  }, [inputValue]);

  // Wrapped setters — changing a filter always returns to page 1.
  const setStatus = useCallback((v: string) => {
    setStatusState(v ?? "");
    setPage(0);
  }, []);
  const setDispatchStatus = useCallback((v: string) => {
    setDispatchStatusState(v ?? "");
    setPage(0);
  }, []);
  const setDateRange = useCallback((r: DateRangeType) => {
    setDateRangeState(r);
    setPage(0);
  }, []);
  const setLimit = useCallback((v: number) => {
    setLimitState(v);
    setPage(0);
  }, []);

  // Sync applied filters + pagination to the URL. `replace: true` keeps the
  // browser history clean — changing a filter should NOT create a new
  // history entry per click.
  //
  // We use the function form of `setSearchParams` and operate ONLY on the
  // keys this hook owns so we never clobber params owned by the page.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of MANAGED_URL_KEYS) next.delete(key);

        if (search) next.set("search", search);
        if (status) next.set("status", status);
        if (dispatchStatus) next.set("dispatchStatus", dispatchStatus);
        if (dateRange.startDate) {
          next.set("startDate", new Date(dateRange.startDate).toISOString());
        }
        if (dateRange.endDate) {
          next.set("endDate", new Date(dateRange.endDate).toISOString());
        }
        if (page > 0) next.set("page", String(page));
        if (limit !== 10) next.set("limit", String(limit));
        return next;
      },
      { replace: true }
    );
  }, [
    search,
    status,
    dispatchStatus,
    dateRange,
    page,
    limit,
    setSearchParams,
  ]);

  const bumpFilterResetKey = useCallback(() => {
    setFilterResetKey((k) => k + 1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setInputValue("");
    setSearchState("");
    setStatusState("");
    setDispatchStatusState("");
    setDateRangeState({ startDate: null, endDate: null });
    setFilterResetKey((k) => k + 1);
    setPage(0);
  }, []);

  const isDateRangeActive = !!(dateRange.startDate || dateRange.endDate);
  const activeFilterCount =
    (search ? 1 : 0) +
    (status ? 1 : 0) +
    (dispatchStatus ? 1 : 0) +
    (isDateRangeActive ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  return {
    inputValue,
    setInputValue,
    search,
    commitSearch,
    status,
    setStatus,
    dispatchStatus,
    setDispatchStatus,
    dateRange,
    setDateRange,
    page,
    setPage,
    limit,
    setLimit,
    filterResetKey,
    bumpFilterResetKey,
    handleClearFilters,
    activeFilterCount,
    hasActiveFilters,
  };
};
